// Convert a UTC instant → the viewer's local YYYY-MM-DD / HH:MM strings.
// All synced rows now carry start_at / end_at as canonical UTC timestamps;
// we shift them to the browser's local timezone here so the calendar grid
// renders the same wall clock the user would see in Google Calendar's web UI.
import { localDate as _localDate, localTime as _localTime } from '../../shared/functions/dateUtils.js'
import { appPageBoilerplateFetch } from '../functions/pageBoilerplate.js'
import { fetchEventDetails as _fetchDetails } from '../functions/calendar/calendarUtils.js'

// Map a Supabase calendar_events row → the camelCase shape the rest of the
// calendar UI expects (legacy from the old in-memory state.js fixture).
export const rowToEvent = (r) => {
  // Prefer the canonical UTC timestamps when present (synced rows). Fall
  // back to the legacy text fields for any rows created before migration
  // 0005 — those are stored in UTC strings already.
  let date = r.date
  let startTime = r.start_time
  let endTime = r.end_time
  let endDate = r.end_date
  if (r.start_at) {
    const s = new Date(r.start_at)
    date = _localDate(s)
    startTime = _localTime(s)
    if (r.end_at) {
      const e = new Date(r.end_at)
      endTime = _localTime(e)
      const ed = _localDate(e)
      if (ed !== date) endDate = ed
    }
  }
  return {
    id: r.id,
    title: r.title,
    date,
    startTime,
    endTime,
    endDate,
    startAt: r.start_at || null,
    endAt: r.end_at || null,
    color: r.color,
    type: r.type || 'meeting',
    // `description` and `invitees` are lazy-loaded by fetchEventDetails
    // when the EventDetailModal opens. `r.description` / `r.invitees`
    // are present here when the row came from a detail fetch.
    description: r.description,
    invitees: r.invitees,
    creatorEmail: r.organizer_email,
    googleEventId: r.google_event_id,
    googleCalendarId: r.google_calendar_id
  }
}

// Multiple rows in calendar_events can carry the same google_event_id —
// each represents one attendee's calendar mirror of the same Google event.
// Collapse them so the UI only shows one card per real meeting. Prefer the
// row mirrored from the current user's calendar when available.
const _dedupeEvents = (rows, currentUserEmail) => {
  const byId = new Map()
  for (const r of rows) {
    const key = r.google_event_id || `local-${r.id}`
    const existing = byId.get(key)
    if (!existing) {
      byId.set(key, r)
      continue
    }
    // If the current user owns one of the duplicates, prefer that row.
    if (r.google_calendar_id === currentUserEmail && existing.google_calendar_id !== currentUserEmail) {
      byId.set(key, r)
    }
  }
  return [...byId.values()]
}

import { freshBooking } from '../functions/calendar/calendarUtils.js'

export const home = {
  extends: 'AppPage',
  metadata: { title: 'Calendar / Symbols Workspace' },

  // Pull people + events from Supabase on page mount. Both write to root
  // state via the `~/` prefix so all child views see the live data.
  fetch: [
    {
      method: 'select',
      from: 'workspaceProject.calendar',
      // Lean projection — drops `description` (16 KB) and `invitees`
      // (92 KB), which together account for 68% of payload. Both are
      // lazy-loaded by EventDetailModal when an event is opened.
      // The grid views filter by `google_calendar_id` (never fall back
      // to invitees in real data — every event has a sync ID).
      select: 'id,title,date,start_time,end_time,end_date,start_at,end_at,color,type,organizer_email,google_event_id,google_calendar_id',
      // Initial load: ±14 days for fast first paint. Wider window can be
      // fetched on demand when the user navigates to a distant week.
      params: (() => {
        const min = new Date(Date.now() - 14 * 86400000).toISOString()
        const max = new Date(Date.now() + 14 * 86400000).toISOString()
        return { deleted: false, start_at: { gte: min, lte: max } }
      })(),
      order: 'start_at',
      limit: 2000,
      as: '~/events',
      cache: '15s',
      // Element-aware transform: read currentUserEmail off root state so the
      // dedupe step can prefer the row from the viewer's own calendar.
      // Keep one row per (calendar × event). Per-view filters in WeekView /
      // TeamView / TeamNowView slice by `googleCalendarId` so each surface
      // shows the events that actually live on the relevant person's calendar
      // — deleting an event from your own calendar makes it disappear from
      // your "My week", even if the same meeting still lives on a coworker's
      // calendar (where it'll still show in their team-view column).
      transform: (rows) => ({ events: (rows || []).map(rowToEvent) })
    },
  ],

  // v3.14: onUpdate only fires on direct .update() calls, not on root state
  // changes. Hash sync + presence use a lightweight 200ms interval instead.
  // Each callback compares against the last-written value and bails when
  // nothing changed, so the interval is effectively free when idle.

  // Tick the "now" indicator every minute. Pulls from the local timezone so
  // the red line tracks the user's actual current time.
  // Also wires up document-level pointer handlers used for moving and
  // resizing events on the week grid (the per-element listeners only fire
  // when the cursor is still over the element — global capture is needed
  // for smooth drag past edges).
  onCreate: (el, s) => {
    // Seed the calendar cursor to today on first mount. Without this,
    // `currentYear/Month/Day` are undefined, `getWeekDays(undefined, ...)`
    // resolves via `new Date(NaN)` and the week grid renders empty day
    // headers + empty columns until the user clicks "Today" or a nav arrow.
    const now = new Date()
    if (
      s.root.currentYear == null ||
      s.root.currentMonth == null ||
      s.root.currentDay == null
    ) {
      s.root.update({
        currentYear: now.getFullYear(),
        currentMonth: now.getMonth(),
        currentDay: now.getDate(),
        viewMode: s.root.viewMode || 'week'
      })
    }

    const tick = () => {
      const t = new Date()
      const m = t.getHours() * 60 + t.getMinutes()
      if (s.root.currentMinutes !== m) s.root.update({ currentMinutes: m })
    }
    el._nowTimer = setInterval(tick, 60 * 1000)

    // Join the shared online-presence channel. Needs the supabase client
    // wired AND currentUserEmail to have landed via the app_settings
    // fetch. Stash a tryJoinPresence closure on `el` so the page-level
    // onUpdate (which fires whenever any reactive root state lands) can
    // retry until both prerequisites are ready — no polling timer needed.
    el._tryJoinPresence = () => {
      if (el._presenceUnsub) return
      const root = el.state.root
      if (!root.currentUserEmail || !el.context?.fetch?.client) return
      el._presenceUnsub = el.call('useOnlinePresence', { app: 'calendar' })
    }
    el._tryJoinPresence()

    // ── Deep-link state via location.hash ──────────────────────────────────
    // Hash format: #view=week&event=123 / #view=team-now&new=1 /
    // #view=week&profile=foo@bar.com — preserved across reloads.
    const VIEWS = new Set(['week', 'month', 'team', 'team-now'])
    const _parseHash = () => {
      const h = (window.location.hash || '').replace(/^#/, '')
      const out = {}
      for (const part of h.split('&').filter(Boolean)) {
        const [k, ...rest] = part.split('=')
        out[k] = decodeURIComponent(rest.join('=') || '')
      }
      return out
    }
    const _writeHash = () => {
      const root = el.state.root
      const parts = []
      if (root.viewMode && root.viewMode !== 'week') parts.push(`view=${root.viewMode}`)
      else parts.push('view=week')
      // Include selected date so reloading lands on the same day
      const y = root.currentYear; const m = root.currentMonth; const d = root.currentDay
      if (y != null && m != null && d != null) {
        const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        const today = new Date()
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        if (dateStr !== todayStr) parts.push(`date=${dateStr}`)
      }
      if (root.showNewBooking) parts.push('new=1')
      else if (root.showEventDetail && root.selectedEvent?.id) parts.push(`event=${root.selectedEvent.id}`)
      else if (root.profileEmail) parts.push(`profile=${encodeURIComponent(root.profileEmail)}`)
      const next = '#' + parts.join('&')
      if (window.location.hash !== next) {
        window.history.replaceState(null, '', next)
      }
    }
    const _applyHash = () => {
      const root = el.state.root
      const q = _parseHash()
      const patch = {}
      const view = q.view && VIEWS.has(q.view) ? q.view : 'week'
      if (root.viewMode !== view) patch.viewMode = view
      // Restore date from hash
      if (q.date) {
        const [y, m, d] = q.date.split('-').map(Number)
        if (y && m && d) {
          if (root.currentYear !== y) patch.currentYear = y
          if (root.currentMonth !== m - 1) patch.currentMonth = m - 1
          if (root.currentDay !== d) patch.currentDay = d
        }
      }
      if (q.new === '1') {
        if (!root.showNewBooking) patch.showNewBooking = true
      } else if (q.event) {
        const id = Number(q.event)
        const ev = (root.events || []).find(e => e.id === id)
        if (ev) {
          patch.selectedEvent = ev
          patch.showEventDetail = true
          if (ev.invitees === undefined) _fetchDetails(id, root)
        } else if (!el._fetchingEvent && (root.events || []).length) {
          // Event not in loaded ±14-day window — fetch it directly via SDK.
          el._fetchingEvent = id
          const sdk = el.context?.methods?.sdk
          const fetchOne = sdk
            ? sdk.execute('workspaceProject.calendar', 'get', { id })
                .then(row => row ? [row] : [])
            : Promise.resolve([])
          fetchOne.then(rows => {
            el._fetchingEvent = null
            if (rows?.length) {
              const mapped = rowToEvent(rows[0])
              root.update({
                events: [...(root.events || []), mapped],
                selectedEvent: mapped,
                showEventDetail: true
              })
            }
          }).catch(() => { el._fetchingEvent = null })
        }
      } else if (q.profile) {
        if (root.profileEmail !== q.profile) patch.profileEmail = q.profile
      } else {
        // No modal in hash → close any open modal that wasn't dismissed
        // through its own button.
        if (root.showNewBooking) patch.showNewBooking = false
        if (root.showEventDetail) { patch.showEventDetail = false; patch.selectedEvent = null }
        if (root.profileEmail) patch.profileEmail = null
      }
      if (Object.keys(patch).length) root.update(patch)
    }
    // Initial restore — events haven't loaded yet on first call. Keep
    // re-applying every 200ms until either (a) the targeted event resolves
    // (modal opened), or (b) the hash has no pending modal, or (c) we've
    // tried for 10 seconds. Stops the moment the modal lands so we don't
    // burn CPU forever.
    el._hashRetryAttempts = 0
    const hashRestoreTick = () => {
      _applyHash()
      const root = el.state.root
      const q = _parseHash()
      const done =
        (q.event && root.showEventDetail) ||
        (q.new === '1' && root.showNewBooking) ||
        (q.profile && root.profileEmail === q.profile) ||
        (!q.event && !q.new && !q.profile)
      el._hashRetryAttempts++
      if (done || el._hashRetryAttempts > 50) {
        clearInterval(el._hashRestoreTimer)
        el._hashRestoreTimer = null
      }
    }
    hashRestoreTick()
    el._hashRestoreTimer = setInterval(hashRestoreTick, 200)
    // Browser back/forward → reapply.
    el._onHashChange = () => _applyHash()
    window.addEventListener('hashchange', el._onHashChange)
    el._writeHash = _writeHash
    // v3.14: onUpdate doesn't fire on root state changes, so we poll at 200ms.
    // _writeHash bails immediately when the hash is unchanged, so cost is ~0.
    el._hashSyncTimer = setInterval(() => {
      _writeHash()
      if (typeof el._tryJoinPresence === 'function') el._tryJoinPresence()
    }, 200)

    const HOUR_PX = 56
    const findEventNode = (target) => target?.closest?.('[data-cal-event-id]')

    const snap15 = (mins) => Math.max(0, Math.min(24 * 60 - 15, Math.round(mins / 15) * 15))
    const minsToTime = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    const timeToMins = (t) => {
      const [h, m] = t.split(':').map(Number)
      return h * 60 + m
    }

    // Debounced popover follow — while the user drags the pending slot, the
    // QuickCreate overlay tracks the cursor but no more than every ~50ms so we
    // don't thrash the reactive `top/left` writes on every mousemove tick.
    el._lastFollowAt = 0
    const FOLLOW_INTERVAL_MS = 50
    el._rafScheduled = false
    el._lastEv = null

    // Look up the column under the cursor without falling back to globals or
    // direct DOM lookups beyond the unavoidable hit-test (`elementFromPoint`).
    const columnDateAt = (clientX, clientY) => {
      const node = document.elementFromPoint(clientX, clientY)?.closest?.('[data-cal-week-col-date]')
      return node?.dataset?.calWeekColDate || null
    }

    el._docMousemove = (ev) => {
      el._lastEv = ev
      const root = el.state.root
      // Nothing to do unless one of the three reactive drag flags is set on
      // root state — see WeekView/CalDragPreview/CalWeekEvent for the
      // mousedown handlers that set them.
      if (!root.activeMove && !root.activePendingMove) return
      if (el._rafScheduled) return
      el._rafScheduled = true
      requestAnimationFrame(() => {
        el._rafScheduled = false
        const e2 = el._lastEv
        if (!e2) return
        _applyDragFrame(e2.clientX, e2.clientY)
      })
    }

    const _applyDragFrame = (clientX, clientY) => {
      const root = el.state.root

      // 1. Pending drag-create slot move (after mouseup, before save).
      const pm = root.activePendingMove
      if (pm) {
        const dp = root.dragPreview
        if (!dp) {
          root.update({ activePendingMove: null })
        } else {
          const dyMins = Math.round((clientY - pm.startY) / HOUR_PX * 60)
          const startMins = timeToMins(pm.origStart)
          const endMins = timeToMins(pm.origEnd)
          const duration = endMins - startMins
          const newStart = snap15(startMins + dyMins)
          const newEnd = Math.min(24 * 60, newStart + duration)
          const newDate = columnDateAt(clientX, clientY) || pm.origDate
          const nextStart = minsToTime(newStart)
          const nextEnd = minsToTime(newEnd)
          const now = Date.now()
          const canFollow = now - el._lastFollowAt > FOLLOW_INTERVAL_MS
          const timeChanged = dp.date !== newDate || dp.startTime !== nextStart || dp.endTime !== nextEnd
          if (timeChanged || canFollow) {
            if (canFollow) el._lastFollowAt = now
            const qc = root.quickCreate
            const followedQC = qc && canFollow
              ? { ...qc, date: newDate, startTime: nextStart, endTime: nextEnd, x: clientX + 12, y: clientY + 12 }
              : (qc && timeChanged
                  ? { ...qc, date: newDate, startTime: nextStart, endTime: nextEnd }
                  : qc)
            root.update({
              dragPreview: timeChanged ? { ...dp, date: newDate, startTime: nextStart, endTime: nextEnd } : dp,
              quickCreate: followedQC
            })
          }
        }
      }

      // 2. Existing-event move/resize.
      const a = root.activeMove
      if (!a) return
      const dyMins = Math.round((clientY - a.startY) / HOUR_PX * 60)
      const startMins = timeToMins(a.origStart)
      const endMins = timeToMins(a.origEnd)
      let newStart, newEnd
      if (a.mode === 'move') {
        const duration = endMins - startMins
        newStart = snap15(startMins + dyMins)
        newEnd = Math.min(24 * 60, newStart + duration)
      } else if (a.mode === 'resize-top') {
        newStart = Math.min(snap15(startMins + dyMins), endMins - 15)
        newEnd = endMins
      } else if (a.mode === 'resize-bottom') {
        newStart = startMins
        newEnd = Math.max(snap15(endMins + dyMins), startMins + 15)
      }
      const newStartStr = minsToTime(newStart)
      const newEndStr = minsToTime(newEnd)
      const newDate = (a.mode === 'move' && columnDateAt(clientX, clientY)) || a.origDate
      const prev = root.dragMove
      if (prev && prev.id === a.id && prev.date === newDate && prev.startTime === newStartStr && prev.endTime === newEndStr) return
      // Reactive in-flight feedback. The original event dims via `dragMove`
      // and a per-column ghost (`CalDragMoveGhost`) renders the dashed
      // preview at the new position. No DOM queries, no inline styles.
      root.update({
        dragMove: { id: a.id, date: newDate, startTime: newStartStr, endTime: newEndStr, mode: a.mode }
      })
    }

    el._docMouseup = () => {
      const root = el.state.root
      const a = root.activeMove
      if (root.activePendingMove) root.update({ activePendingMove: null })
      document.body.style.cursor = ''
      if (!a) return
      // Read the latest in-flight position from `dragMove` so we don't
      // duplicate the cursor math here.
      const dm = root.dragMove
      const orig = { date: a.origDate, startTime: a.origStart, endTime: a.origEnd }
      const next = (dm && dm.id === a.id)
        ? { date: dm.date, startTime: dm.startTime, endTime: dm.endTime }
        : orig
      const noChange = orig.date === next.date && orig.startTime === next.startTime && orig.endTime === next.endTime
      if (noChange) {
        root.update({ activeMove: null, dragMove: null })
        return
      }
      // Clear the live drag flags — the reactive top/height getters on
      // CalWeekEvent take over once we commit to root.events below.
      root.update({ activeMove: null, dragMove: null })
      const events = root.events || []
      const idx = events.findIndex(x => x.id === a.id)
      if (idx === -1) return
      const ev2 = events[idx]
      const me = root.currentUserEmail
      // Proposals themselves: tweaking a proposal just updates it; commit
      // and skip the prompt logic.
      if (ev2.isProposal) {
        const updatedEvents = events.slice()
        updatedEvents[idx] = { ...ev2, ...next }
        root.update({ events: updatedEvents })
        return
      }
      const otherInvitees = (ev2.invitees || []).filter(e => e && e !== me)
      const isCreator = !ev2.creatorEmail || ev2.creatorEmail === me
      if (isCreator && otherInvitees.length === 0) {
        // Creator + nobody else involved → commit silently to local + Supabase.
        const updatedEvents = events.slice()
        const movedEv = { ...ev2, ...next }
        updatedEvents[idx] = movedEv
        root.update({ events: updatedEvents })
        el.call('saveEventToSupabase', movedEv)
        return
      }
      if (isCreator) {
        // Creator + other participants → ask whether to update outright or
        // propose. Do NOT commit the move yet; the modal's Update/Propose
        // buttons handle the commit so the original stays put until the
        // creator decides.
        root.update({
          confirmMove: { eventId: ev2.id, orig, next }
        })
        return
      }
      // Non-creator: don't touch the original. Add a proposal duplicate at
      // the new position; it renders with reduced opacity until the creator
      // (or another invitee) accepts it from the event detail modal.
      const proposal = {
        ...ev2,
        ...next,
        id: Date.now(),
        isProposal: true,
        originalEventId: ev2.id,
        proposedBy: me
      }
      root.update({ events: [...events, proposal] })
      el.call('saveEventToSupabase', proposal).then((realId) => {
        if (!realId) return
        root.update({
          events: (root.events || []).map(e =>
            e.id === proposal.id ? { ...e, id: realId } : e
          )
        })
      })
    }

    document.addEventListener('mousemove', el._docMousemove)
    document.addEventListener('mouseup', el._docMouseup)
  },
  onRemove: (el) => {
    if (el._nowTimer) clearInterval(el._nowTimer)
    if (el._hashSyncTimer) clearInterval(el._hashSyncTimer)
    if (typeof el._presenceUnsub === 'function') el._presenceUnsub()
    if (el._hashRestoreTimer) clearInterval(el._hashRestoreTimer)
    if (el._onHashChange) window.removeEventListener('hashchange', el._onHashChange)
    if (el._docMousemove) document.removeEventListener('mousemove', el._docMousemove)
    if (el._docMouseup) document.removeEventListener('mouseup', el._docMouseup)
  },

  CalSidebar: {},
  CalMain: {},
  // AI Assistant + ProfileModal are mounted at app level in app.js.
  CalNewBookingModal: {},
  CalConfirmMoveModal: {},
  CalEventDetailModal: {},
  // Person flyout — shared-gov's SharedPersonCard with calendar-
  // specific Actions (Book + Profile). The shared chrome handles
  // positioning + outside-click dismissal; we only customize the
  // bottom-row buttons.
  PersonCard: {
    extends: 'SharedPersonCard',

    Actions: {
      BookBtn: {
        tag: 'button',
        text: 'Book',
        flex: '1',
        padding: 'Z A',
        round: 'Z',
        border: 'none',
        theme: 'primary',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        ':hover': { opacity: '.9' },
        onClick: (e, el, s) => {
          const person = s.root.selectedPerson
          if (!person) return
          s.root.update({
            selectedPerson: null,
            showNewBooking: true,
            newBooking: freshBooking({
              title: `Meeting with ${person.name}`,
              invitees: person.email,
              type: 'meeting'
            })
          })
        }
      },

      ProfileBtn: {
        tag: 'button',
        text: 'Profile',
        flex: '1',
        padding: 'Z A',
        round: 'Z',
        theme: 'secondary',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '500',
        ':hover': { background: 'overlay' },
        onClick: (e, el, s) => {
          const email = s.root.selectedPerson?.email
          s.root.update({ selectedPerson: null, profileEmail: email })
        }
      }
    }
  },
  CalQuickCreate: {}
}
